import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

// Shared confirmation dialog for deleting a reviewable paragraph during
// AI-output review.
//
// Why this exists:
// RD_SSI.js (ADHD Strengths) hand-rolls this exact Radix AlertDialog inline. The
// Autism OBS Self-Report delete needs the same confirm step, so rather than
// paste a second copy the markup lives here once. Kept deliberately presentational
// and content-agnostic (title / description / confirm label are all props) so the
// same shell serves both flows and any future paragraph delete. RD_SSI is left on
// its inline copy for now to avoid touching a live ADHD flow in this change; it is
// the intended migration target for this component.
//
// Copy note: the caller owns all wording, including any "last paragraph" warning,
// because the consequence of deleting the final paragraph differs per module.

/**
 * @param {Object} props
 * @param {boolean} props.open            Whether the dialog is visible.
 * @param {(open: boolean) => void} props.onOpenChange  Radix open-state change handler
 *   (called with false on overlay/Escape/Cancel dismissal).
 * @param {() => void} props.onConfirm    Runs the deletion when the clinician confirms.
 * @param {string} props.title            Dialog heading.
 * @param {string} props.description      Explanatory body copy.
 * @param {string} [props.confirmLabel='Delete']  Confirm button label.
 */
export function ParagraphDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Delete',
}) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[420px] max-w-[90vw] z-50 shadow-xl">
          <AlertDialog.Title className="text-lg font-bold mb-2">
            {title}
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mb-5">
            {description}
          </AlertDialog.Description>
          <div className="flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                onClick={onConfirm}
                className="py-2 px-4 font-semibold rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors"
              >
                {confirmLabel}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
